import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Sparkles, ArrowLeft, ArrowRight, RotateCcw } from 'lucide-react';
import { motion } from 'motion/react';
import { Box, Typography, Stack, Card, Link as MuiLink, Alert } from '@mui/material';
import { supabase } from '../lib/supabase';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;
        setError(null);

        setIsLoading(true);
        try {
            const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/auth/reset-password`,
            });
            if (resetError) throw resetError;
            navigate('/auth/email-confirmation', { state: { email } });
        } catch (err: any) {
            setError(err.message || 'Failed to send reset email');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
            {/* Top Logo - Centered */}
            <Box sx={{ pt: 6, display: 'flex', justifyContent: 'center' }}>
                <Stack direction="row" alignItems="center" spacing={1} component={Link} to="/" sx={{ textDecoration: 'none' }}>
                    <Box sx={{ bgcolor: '#4f46e5', p: 0.5, borderRadius: 1.5, display: 'flex' }}>
                        <Sparkles color="white" size={24} />
                    </Box>
                    <Typography variant="h5" fontWeight="bold" letterSpacing="-0.02em" sx={{ color: '#111827' }}>
                        PostPilot <Box component="span" sx={{ color: '#4f46e5' }}>AI</Box>
                    </Typography>
                </Stack>
            </Box>

            {/* Main Content */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, pt: 6 }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: '100%', maxWidth: 460 }}
                >
                    <Card elevation={0} sx={{ borderRadius: 4, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', bgcolor: 'white', overflow: 'hidden' }}>

                        {/* Top decorative gradient header */}
                        <Box sx={{
                            height: 120,
                            background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                        }}>
                            <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                bgcolor: '#e0e7ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                border: '4px solid white'
                            }}>
                                <RotateCcw size={24} color="#4f46e5" />
                            </Box>
                        </Box>

                        <Box sx={{ p: { xs: 4, sm: 5 } }}>
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h5" fontWeight={800} sx={{ color: '#111827', mb: 1, letterSpacing: '-0.02em' }}>
                                    Forgot Password?
                                </Typography>
                                <Typography variant="body2" color="#475569" sx={{ lineHeight: 1.6 }}>
                                    No worries! Enter your email address below and we'll send you a secure link to reset your password.
                                </Typography>
                            </Box>

                            {error && (
                                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={700} color="#334155" sx={{ mb: 1 }}>Email Address</Typography>
                                        <Input
                                            type="email"
                                            placeholder="name@company.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                        />
                                    </Box>

                                    <Button type="submit" size="lg" sx={{ width: '100%', bgcolor: '#4f46e5', color: 'white', '&:hover': { bgcolor: '#4338ca' }, py: 1.5, fontWeight: 'bold', fontSize: '1rem' }} isLoading={isLoading}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            Send Reset Link <ArrowRight size={18} />
                                        </Box>
                                    </Button>
                                </Stack>
                            </form>

                            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
                                <MuiLink component={Link} to="/auth" variant="body2" fontWeight={600} sx={{ color: '#4f46e5', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover': { textDecoration: 'underline' } }}>
                                    <ArrowLeft size={16} /> Back to Login
                                </MuiLink>
                            </Box>
                        </Box>
                    </Card>
                </motion.div>

                <Box sx={{ mt: 4 }}>
                    <Typography variant="caption" color="#94a3b8">
                        Need help? <MuiLink href="#" color="#4f46e5" fontWeight={500} underline="hover">Contact Support</MuiLink>
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
